import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trophy, Target, TrendingUp, Zap, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

export function TopTipOfDay() {
  // This would come from database in production
  const topTip = {
    id: 'totd1',
    title: 'Manchester United vs Liverpool',
    prediction: 'Over 2.5 Goals',
    sport: 'Football',
    league: 'Premier League',
    odds: 2.15,
    confidence: 94,
    reasoning: 'Both teams averaging 3.2 goals per game in last 5 matches. Historical head-to-head shows 87% of games had 3+ goals. Key attackers all fit and in form.',
    tipsterName: 'Marcus "The Shark" Silva',
    tipsterAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Marcus',
    tipsterWinRate: 82,
    tipsterROI: 34.5,
    isPremium: true,
    kickoff: '8 hours',
  };

  return (
    <Card className="relative overflow-hidden bg-gradient-to-br from-yellow-500/10 via-orange-500/5 to-red-500/10 border-yellow-500/30 hover:shadow-2xl transition-all duration-500">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-orange-500/5 animate-pulse" />
      
      {/* Badge */}
      <div className="absolute top-4 right-4 z-10">
        <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-bold animate-bounce">
          <Trophy className="w-3 h-3 mr-1" />
          TIP OF THE DAY
        </Badge>
      </div>

      <CardContent className="relative z-10 p-6 space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              ⚽ {topTip.sport}
            </span>
            <span>•</span>
            <span>{topTip.league}</span>
            <span>•</span>
            <span>Kickoff in {topTip.kickoff}</span>
          </div>
          
          <h2 className="text-2xl font-bold">{topTip.title}</h2>
          
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-lg font-bold px-3 py-1 bg-primary/10">
              {topTip.prediction}
            </Badge>
            <Badge className="bg-green-500/20 text-green-700 border-green-500/30">
              {topTip.odds}x odds
            </Badge>
          </div>
        </div>

        {/* Confidence Meter */}
        <div className="space-y-2 bg-card/50 rounded-lg p-4 border border-border/50">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" />
              <span className="font-medium">Confidence Level</span>
            </div>
            <span className="text-lg font-bold text-primary">{topTip.confidence}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-1000 ease-out"
              style={{ width: `${topTip.confidence}%` }}
            />
          </div>
        </div>

        {/* Reasoning */}
        <div className="bg-card/50 rounded-lg p-4 border border-border/50">
          <p className="text-sm text-muted-foreground italic leading-relaxed">
            "{topTip.reasoning}"
          </p>
        </div>

        {/* Tipster Info */}
        <div className="flex items-center justify-between p-4 bg-card/50 rounded-lg border border-border/50">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 ring-2 ring-yellow-500/50">
              <AvatarImage src={topTip.tipsterAvatar} />
              <AvatarFallback className="bg-primary/10">MS</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold">{topTip.tipsterName}</p>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Target className="w-3 h-3" />
                  {topTip.tipsterWinRate}% WR
                </span>
                <span className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  +{topTip.tipsterROI}% ROI
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="flex gap-3">
          <Button
            size="lg"
            className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
            asChild
          >
            <Link to="/tips" className="flex items-center justify-center gap-2">
              {topTip.isPremium && <Lock className="w-4 h-4" />}
              Get Full Analysis
              <Zap className="w-4 h-4" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
